import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Layers, Monitor, Gauge, Trophy, Sparkles, Zap } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import RobotRecommendationWidget from "@/components/RobotRecommendationWidget";
import RobotPageNav from "@/components/RobotPageNav";
import pudubot2Img from "@/assets/robots/pudubot-2.jpg";

const PuduBot2 = () => {
  const specifications = [
    {
      icon: <Monitor className="w-5 h-5" />,
      label: "Display",
      value: "Smart Dot Matrix"
    },
    {
      icon: <Layers className="w-5 h-5" />,
      label: "Tray Detection",
      value: "4-Layer Infrared"
    },
    {
      icon: <Gauge className="w-5 h-5" />,
      label: "Navigation",
      value: "Advanced SLAM"
    },
    {
      icon: <Trophy className="w-5 h-5" />,
      label: "Award",
      value: "IDEA Winner"
    }
  ];

  const keyFeatures = [
    {
      icon: <Monitor className="w-6 h-6" />,
      title: "Smart Dot Matrix Screen",
      description: "Innovative dot matrix display provides dynamic expressions and customizable messaging that creates engaging customer interactions and memorable experiences."
    },
    {
      icon: <Layers className="w-6 h-6" />,
      title: "Four-Layer Infrared Tray Sensor",
      description: "Advanced four-layer infrared sensing system accurately detects item placement and removal across all trays for precise delivery tracking."
    },
    {
      icon: <Gauge className="w-6 h-6" />,
      title: "Advanced Navigation System",
      description: "State-of-the-art SLAM navigation with obstacle avoidance ensures smooth, reliable autonomous movement through dynamic environments."
    },
    {
      icon: <Trophy className="w-6 h-6" />,
      title: "IDEA Award Winner",
      description: "Internationally recognized design excellence with award-winning aesthetics that enhance any hospitality environment."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Universal Delivery Platform",
      description: "Versatile platform suitable for diverse delivery tasks from food and beverage to cleaning and logistics across multiple industries."
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Enhanced Efficiency",
      description: "Streamline operations with autonomous delivery that reduces labor costs while improving service speed and consistency."
    }
  ];

  const applications = [
    {
      title: "Full-Service Restaurants",
      description: "Deliver food, beverages, and clear dishes efficiently while maintaining high service standards and improving table turnover."
    },
    {
      title: "Quick Service Restaurants",
      description: "Fast order delivery to tables and efficient queue management during peak hours to maximize customer satisfaction."
    },
    {
      title: "Hotels & Hospitality",
      description: "Room service delivery, amenity distribution, and guest services to enhance hospitality experiences and operational efficiency."
    },
    {
      title: "Healthcare Facilities",
      description: "Medicine delivery, supply transport, and meal distribution in hospitals and care facilities with reliable autonomous navigation."
    },
    {
      title: "Retail & Shopping",
      description: "Product delivery, inventory transport, and customer service enhancement in retail environments and shopping centers."
    },
    {
      title: "Corporate Offices",
      description: "Mail delivery, supply distribution, and inter-department transport to improve workplace efficiency and productivity."
    }
  ];

  return (
    <>
      <SEO
        title="PuduBot 2 - Universal Delivery Robot | Neil AI"
        description="Award-winning universal delivery robot with smart dot matrix screen and four-layer infrared tray sensor. IDEA Award Winner. Perfect for restaurants, hotels, and more."
        keywords="PuduBot 2, delivery robot, universal robot, smart delivery, IDEA award, restaurant robot, hotel robot"
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        <RobotRecommendationWidget />
        
        <main className="container mx-auto px-4 pt-12 pb-8 md:pt-16 md:pb-12">
          <RobotPageNav />

          {/* Hero Section */}
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 mb-12">
            <div className="space-y-6">
              <div>
                <Badge className="mb-4">Universal Delivery Robot</Badge>
                <h1 className="font-orbitron font-bold text-2xl md:text-4xl lg:text-5xl mb-4 break-words">
                  PuduBot 2
                </h1>
                <p className="text-base md:text-lg lg:text-xl text-muted-foreground leading-relaxed">
                  Award-winning universal delivery robot with smart dot matrix screen and advanced navigation. IDEA Award Winner for exceptional design and functionality.
                </p>
              </div>

              {/* Quick Specs */}
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                {specifications.map((spec, idx) => (
                  <Card key={idx} className="glass-effect border-[hsl(var(--line))]">
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-center gap-2 mb-2 text-primary">
                        {spec.icon}
                        <span className="text-xs font-semibold">{spec.label}</span>
                      </div>
                      <p className="text-xs md:text-sm font-bold">{spec.value}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <Button asChild size="lg" className="flex-1">
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                    Book a Demo
                  </a>
                </Button>
                <Button asChild variant="outline" size="lg" className="flex-1">
                  <a href="https://myascentium.com/ApplyNow/CommCredit?v=b106671d-0a80-40ca-aea6-5c888e426df6&s=dca75d8e-7184-4ed5-a7d0-f35ed9e35e86" target="_blank" rel="noopener noreferrer">
                    Apply for Financing
                  </a>
                </Button>
                <Button asChild variant="secondary" size="lg" className="flex-1">
                  <a href="tel:+13369624539">
                    Call Us: (336) 962-4539
                  </a>
                </Button>
              </div>

              {/* ROI Highlight */}
              <Card className="glass-effect border-primary/30 bg-primary/5">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold mb-1 text-sm md:text-base">ROI Impact</h3>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        Reduce operational costs by 45% while improving delivery efficiency. Typical ROI achieved within 10-14 months through labor savings and increased throughput.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Product Image */}
            <div className="relative">
              <div className="aspect-square md:aspect-auto md:h-full rounded-lg overflow-hidden bg-white/5 flex items-center justify-center p-4 md:p-8">
                <img 
                  src={pudubot2Img} 
                  alt="PuduBot 2 - Universal Delivery Robot"
                  className="w-full h-full object-cover"
                />
              </div>
              <Badge className="absolute top-4 right-4 bg-primary text-primary-foreground">
                IDEA AWARD WINNER
              </Badge>
            </div>
          </div>

          {/* Detailed Information Tabs */}
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="specs">Specifications</TabsTrigger>
              <TabsTrigger value="applications">Applications</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron">Key Features</CardTitle>
                  <CardDescription>
                    Award-winning design meets exceptional functionality
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
                    {keyFeatures.map((feature, idx) => (
                      <div key={idx} className="flex gap-3 md:gap-4">
                        <div className="text-primary flex-shrink-0">{feature.icon}</div>
                        <div>
                          <h4 className="font-bold mb-1 md:mb-2 text-sm md:text-base">{feature.title}</h4>
                          <p className="text-xs md:text-sm text-muted-foreground">{feature.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="specs" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron">Technical Specifications</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 md:space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pb-3 md:pb-4 border-b border-[hsl(var(--line))]">
                      <span className="font-semibold text-sm md:text-base">Display Technology</span>
                      <span className="text-sm md:text-base">Smart Dot Matrix Screen</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pb-3 md:pb-4 border-b border-[hsl(var(--line))]">
                      <span className="font-semibold text-sm md:text-base">Tray Detection</span>
                      <span className="text-sm md:text-base">Four-Layer Infrared Sensor</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pb-3 md:pb-4 border-b border-[hsl(var(--line))]">
                      <span className="font-semibold text-sm md:text-base">Navigation System</span>
                      <span className="text-sm md:text-base">Advanced SLAM Technology</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pb-3 md:pb-4 border-b border-[hsl(var(--line))]">
                      <span className="font-semibold text-sm md:text-base">Obstacle Detection</span>
                      <span className="text-sm md:text-base">360° Multi-Sensor System</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pb-3 md:pb-4 border-b border-[hsl(var(--line))]">
                      <span className="font-semibold text-sm md:text-base">Design Recognition</span>
                      <span className="text-sm md:text-base">IDEA Award Winner</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pb-3 md:pb-4 border-b border-[hsl(var(--line))]">
                      <span className="font-semibold text-sm md:text-base">Deployment Type</span>
                      <span className="text-sm md:text-base">Universal Multi-Purpose</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                      <span className="font-semibold text-sm md:text-base">Safety Features</span>
                      <span className="text-sm md:text-base">Comprehensive Obstacle Avoidance</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="applications" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron">Ideal Applications</CardTitle>
                  <CardDescription>
                    Versatile delivery solution for multiple industries
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
                    {applications.map((app, idx) => (
                      <div key={idx} className="p-3 md:p-4 rounded-lg border border-[hsl(var(--line))] hover:border-primary/50 transition-colors">
                        <h4 className="font-bold mb-2 text-sm md:text-base">{app.title}</h4>
                        <p className="text-xs md:text-sm text-muted-foreground">{app.description}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Final CTA */}
          <Card className="glass-effect border-primary/30 bg-primary/5 mt-8 md:mt-12">
            <CardContent className="p-6 md:p-8 text-center">
              <h2 className="font-orbitron font-bold text-xl md:text-2xl mb-4">
                Ready to Experience Award-Winning Delivery?
              </h2>
              <p className="text-sm md:text-base text-muted-foreground mb-6 max-w-2xl mx-auto">
                Join leading businesses worldwide using PuduBot 2 to transform their delivery operations with award-winning technology.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
                <Button asChild size="lg">
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                    Schedule a Demo
                  </a>
                </Button>
                <Button asChild variant="secondary" size="lg">
                  <Link to="/robot-recommender">
                    Find Your Perfect Robot
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default PuduBot2;
